import api from "../api/api";
import { AxiosError } from "axios";

// Define the shape of login credentials
interface LoginCredentials {
  username: string;
  password: string;
}

// Define the shape of a successful login response
export interface LoginSuccess {
  success?: boolean;
  message: string;
  data?: {
    access: string;
    refresh: string;
  };
}

// Define the return type (either success or error)
type LoginResult = any;

// Token refresh timer from environment
const TOKEN_REFRESH_TIMER = parseInt(
  import.meta.env.VITE_TOKEN_REFRESH_TIMER
);

// Store refresh timer reference
let refreshTimer: number | null = null;

// Clear refresh timer
export function clearRefreshTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

// Start token refresh timer
function startRefreshTimer() {
  clearRefreshTimer();
  refreshTimer = setInterval(async () => {
    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      try {
        await handleRefreshToken();
      } catch (error) {
        console.error("Token refresh failed:", error);
        // If refresh fails, the 401 interceptor will handle logout
      }
    }
  }, TOKEN_REFRESH_TIMER);
}

// Handle token refresh
export async function handleRefreshToken(): Promise<void> {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token found");
    }
    const res = await api.post<any>("/api/v1/auth/refresh/", { refresh: refreshToken });
    
    if (res.data && res.data.access) {
      localStorage.setItem("access_token", res.data.access);
      api.defaults.headers.common["Authorization"] = `Bearer ${res.data.access}`;
      console.log("Token refreshed successfully");
    }
  } catch (error) {
    console.error("Token refresh error:", error);
    throw error;
  }
}

// Helper to decode JWT
function parseJwt(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

export default async function handleLogin(
  credentials: LoginCredentials
): Promise<LoginResult> {
  try {
    const res = await api.post<LoginSuccess>("/api/v1/auth/login/", credentials);

    const data: any = res.data;
    if (data && data.access) {
      const accessToken = data.access;
      const refreshToken = data.refresh;
      
      if (accessToken) {
        const decoded = parseJwt(accessToken);
        const userId = decoded?.user_id || "";
        
        console.log("Login successful", userId);

        localStorage.setItem("access_token", accessToken);
        if (refreshToken) {
            localStorage.setItem("refresh_token", refreshToken);
        }
        localStorage.setItem("user_id", String(userId));
        localStorage.setItem("is_admin", String(decoded?.is_superuser || false));

        // Set default authorization header for all future requests
        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
        
        // Start token refresh timer
        startRefreshTimer();
        
        return {
            message: "Login successful",
            user_id: userId,
            is_admin: decoded?.is_superuser || false,
            access_token: accessToken,
            token_type: "bearer"
        };
      }
      return { message: "Invalid response format" };
    } else {
      console.error("Login failed, no access token");
      return { message: "Login failed" };
    }
  } catch (err: unknown) {
    const error = err as AxiosError<{ message?: string; detail?: string }>;
    console.error("Login error:", error.response?.data || error.message);
    return { message: error.response?.data?.message || error.response?.data?.detail || "Login failed" };
  }
}
